import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type CreateOrganisationAccountingCategoryRequest,
  type OrganisationAccountingCategory,
  type UpdateOrganisationAccountingCategoryRequest,
} from '../client';

/** Stable query-key factory for accounting-category caches. */
export const organisationAccountingCategoryKeys = {
  all: ['directory', 'organisation-accounting-category'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-accounting-category', 'org', organisationId] as const,
};

/** Every accounting category for an organisation. Pass `null` to keep disabled. */
export function useOrganisationAccountingCategories(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<OrganisationAccountingCategory[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrganisationAccountingCategory[], Error>({
    queryKey: organisationId
      ? organisationAccountingCategoryKeys.byOrganisation(organisationId)
      : organisationAccountingCategoryKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationAccountingCategories(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateOrganisationAccountingCategory() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateOrganisationAccountingCategoryRequest>({
    mutationKey: [...organisationAccountingCategoryKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createOrganisationAccountingCategory(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: organisationAccountingCategoryKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/** The caller passes `organisationId` so the per-org list refreshes. */
export function useUpdateOrganisationAccountingCategory() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateOrganisationAccountingCategoryRequest }
  >({
    mutationKey: [...organisationAccountingCategoryKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateOrganisationAccountingCategory(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationAccountingCategoryKeys.byOrganisation(organisationId),
      });
    },
  });
}

export function useDeleteOrganisationAccountingCategory() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...organisationAccountingCategoryKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteOrganisationAccountingCategory(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationAccountingCategoryKeys.byOrganisation(organisationId),
      });
    },
  });
}
